import { useUserContext } from '@lemonade-hq/nuiza';
import { toInitials } from '@lemonade-hq/ts-helpers';
import type { HeaderMenuProps, UniclientAction } from '@lemonade-hq/uniclient-chat';
import type { IconButtonVariants } from '@lemonade-hq/uniclient-theme';
import {
  ellipsis,
  Flex,
  Icon,
  IconButton,
  Layout,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  spacing,
  Text,
  useViewportSize,
} from '@lemonade-hq/uniclient-theme';
import React from 'react';
import type { HeaderPredefinedMenuActions, PredefinedHeaderAction } from '../hooks/useWizardMenu';
import { useWizardMenu } from '../hooks/useWizardMenu';
import * as styles from './WizardHeaderMenu.css';

interface UserNameProps {
  readonly userName?: string;
}

const UserName: React.FC<UserNameProps> = ({ userName }) => {
  const { isMobileViewport: showInitials } = useViewportSize();

  return (
    <Text className={styles.userName} type="h6">
      {showInitials ? toInitials(userName) : userName}
    </Text>
  );
};

interface MenuActionItemProps {
  readonly action: PredefinedHeaderAction;
}

const MenuActionItem: React.FC<MenuActionItemProps> = ({ action }) => (
  <MenuItem disabled={action.disabled} onClick={() => void action.onAction?.()}>
    <Flex alignItems="center" gap={spacing.s12} width="100%">
      {action.icon && <Icon name={action.icon} size="md" />}
      <Text className={ellipsis} type="bodyMedium">
        {action.title}
      </Text>
    </Flex>
  </MenuItem>
);

export interface WizardHeaderMenuProps {
  readonly menuAction: HeaderMenuProps;
  readonly performAction: (action?: UniclientAction | undefined) => Promise<unknown>;
  readonly predefinedActions?: HeaderPredefinedMenuActions;
  readonly menuIconButtonSize: NonNullable<IconButtonVariants>['size'];
}

export const WizardHeaderMenu: React.FC<WizardHeaderMenuProps> = ({
  menuAction,
  predefinedActions,
  performAction,
  menuIconButtonSize,
}) => {
  const menuActions = useWizardMenu({
    predefinedActions,
    subActions: menuAction.subActions,
    performAction,
  });
  const { user } = useUserContext();
  if (menuActions.length === 0) {
    return null;
  }

  const displayUser = user != null && Boolean(menuAction.showUserName);
  const hasSections = menuActions.some(item => 'actions' in item);

  return (
    <Menu>
      <Flex alignItems="center" as={MenuTrigger} className={styles.menuTrigger} hideChevron={!displayUser} p={0}>
        {displayUser ? (
          <UserName userName={`${user.givenName} ${user.familyName}`} />
        ) : (
          <IconButton as="div" background="transparent" icon={menuAction.icon} size={menuIconButtonSize} />
        )}
      </Flex>

      <MenuContent align="end" className={styles.menuContent} {...(hasSections && { style: { gap: spacing.s16 } })}>
        {menuActions.map(item => {
          if ('actions' in item) {
            return (
              <Flex flexDirection="column" key={item.title}>
                <Layout className={styles.menuSectionTitle}>
                  <Text type="h5">{item.title}</Text>
                </Layout>
                {item.actions.map(action => (
                  <MenuActionItem action={action} key={action.title} />
                ))}
              </Flex>
            );
          } else {
            return <MenuActionItem action={item} key={item.title} />;
          }
        })}
      </MenuContent>
    </Menu>
  );
};
